import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useSnackbar } from '@/hooks/common/useSnackbar';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { AdditionalExpenseItem, EstimatedRepairCategory } from '@otw/models';
import {
  additionalExpenseItemConverter,
  parseEstimatedRepairTime,
  parseEstimatedRepairTimeUnit,
} from '@otw/models/converters';

interface UseExpenseSettingsOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  dontShowUpdates?: boolean;
  enabled?: boolean;
}

const EXPENSE_SETTINGS_COLLECTION = 'expense_settings';

const isEstimatedRepairCategory = (value: unknown): value is EstimatedRepairCategory =>
  value === 'body' || value === 'mechanical' || value === 'safety';

const hoursEqual = (a: number | null | undefined, b: number | null | undefined) =>
  (a ?? null) === (b ?? null);

const itemsEqual = (a: AdditionalExpenseItem, b: AdditionalExpenseItem) =>
  a.name === b.name &&
  a.amount === b.amount &&
  a.category === b.category &&
  hoursEqual(a.estimated_repair_time, b.estimated_repair_time) &&
  parseEstimatedRepairTimeUnit(a.estimated_repair_time_unit) ===
    parseEstimatedRepairTimeUnit(b.estimated_repair_time_unit);

export const useExpenseSettings = (options?: UseExpenseSettingsOptions) => {
  const { showSuccess, showError } = useSnackbar();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  const queryKey = ['expense_settings'] as const;
  const settingsCollection = collection(db, EXPENSE_SETTINGS_COLLECTION);
  const settingsQuery = settingsCollection.withConverter(additionalExpenseItemConverter);

  const {
    data: additionalExpenseItems = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<AdditionalExpenseItem[]> => {
      const snapshot = await getDocs(settingsQuery);
      return snapshot.docs
        .map(docSnap => docSnap.data())
        .filter(item => Boolean(item.name))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    },
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled !== false,
  });

  const saveAdditionalExpenseItems = useMutation({
    mutationFn: async (items: AdditionalExpenseItem[]) => {
      const now = new Date().toISOString();
      const uid = currentUser?.uid || '';
      const existingById = new Map(additionalExpenseItems.map(item => [item.id, item]));
      const keptExistingIds = new Set(
        items.filter(item => existingById.has(item.id)).map(item => item.id),
      );

      const deleteWrites = additionalExpenseItems
        .filter(existing => !keptExistingIds.has(existing.id))
        .map(existing => deleteDoc(doc(db, EXPENSE_SETTINGS_COLLECTION, existing.id)));

      await Promise.all(deleteWrites);

      const savedItems: AdditionalExpenseItem[] = [];

      for (const item of items) {
        const name = item.name.trim();
        const amount = Number(item.amount) || 0;
        const category = isEstimatedRepairCategory(item.category) ? item.category : 'safety';
        const estimated_repair_time = parseEstimatedRepairTime(item.estimated_repair_time);
        const estimated_repair_time_unit = parseEstimatedRepairTimeUnit(
          item.estimated_repair_time_unit,
        );

        if (!name) {
          throw new Error('Each expense setting item requires a name');
        }
        if (Number.isNaN(amount) || amount < 0) {
          throw new Error(`Invalid amount for "${name}"`);
        }
        if (
          estimated_repair_time != null &&
          (Number.isNaN(estimated_repair_time) || estimated_repair_time < 0)
        ) {
          throw new Error(`Invalid estimated repair time for "${name}"`);
        }

        const existing = existingById.get(item.id);

        if (existing) {
          if (
            !itemsEqual(existing, {
              ...existing,
              name,
              amount,
              category,
              estimated_repair_time,
              estimated_repair_time_unit,
            })
          ) {
            await updateDoc(doc(db, EXPENSE_SETTINGS_COLLECTION, existing.id), {
              name,
              amount,
              category,
              estimated_repair_time,
              estimated_repair_time_unit,
              updated_at: now,
              updated_by: uid,
            });
          }

          savedItems.push({
            ...existing,
            name,
            amount,
            category,
            estimated_repair_time,
            estimated_repair_time_unit,
            updated_at: now,
            updated_by: uid,
          });
          continue;
        }

        const docRef = await addDoc(settingsCollection, {
          name,
          amount,
          category,
          estimated_repair_time,
          estimated_repair_time_unit,
          created_at: now,
          created_by: uid,
          updated_at: now,
          updated_by: uid,
        });

        savedItems.push({
          id: docRef.id,
          name,
          amount,
          category,
          estimated_repair_time,
          estimated_repair_time_unit,
          created_at: now,
          created_by: uid,
          updated_at: now,
          updated_by: uid,
        });
      }

      return savedItems.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      );
    },
    onSuccess: savedItems => {
      if (!options?.dontShowUpdates) {
        showSuccess('Expense settings updated successfully');
      }

      queryClient.setQueryData(queryKey, savedItems);
      options?.onSuccess?.();
    },
    onError: err => {
      if (!options?.dontShowUpdates) {
        showError('Error updating expense settings');
      }

      console.error('Error updating expense settings:', err);
      options?.onError?.(err as Error);
    },
  });

  return {
    additionalExpenseItems,
    isLoading,
    isError,
    error,
    saveAdditionalExpenseItems: saveAdditionalExpenseItems.mutate,
    saveAdditionalExpenseItemsAsync: saveAdditionalExpenseItems.mutateAsync,
    isUpdating: saveAdditionalExpenseItems.isPending,
    refetch,
  };
};
